// apps/web/src/pages/coletor/kanban-tablet/tablet-concluir-sheet.tsx — Fase 11, Plano 15 (KAN-06, D-17, D-22, T-11-57);
// Fase 19, Plano 07 (D-10, D-12, D-16, D-17): pré-preenchido com o que chegou, 1 toque, sem teclado automático.
// Apontamento atômico pré-preenchido: com tudo certo, 1 toque confirma.
import { useState, type ReactElement } from 'react';
import type { KanbanCartaoTablet } from '@pieltec/contracts';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import {
  avaliarConfirmacao,
  converterQuantidade,
  descreverEtapasPuladas,
  descricaoConcluir,
  rotuloBotaoConcluir,
  valoresIniciaisConclusao,
  type ModoConcluir,
  type ValoresConclusao,
} from '../../../lib/kanban/tablet';

export const AVISO_CABINE_ALOCADA = 'Ao registrar, o cartão fica nesta cabine.';

export interface TabletConcluirSheetProps {
  cartao: KanbanCartaoTablet;
  /** 'registrar' = "Registrar e avançar" a partir da lista de etapas anteriores (D-16). */
  modo?: ModoConcluir;
  /** Chave estável por (cartão, conclusão pendente), emitida pela página (CR-WEB-02). */
  identificadorIdempotente: string;
  enviando: boolean;
  erro: string | null;
  onFechar: () => void;
  /** A chave de idempotência é a mesma em reaberturas e reenvios da mesma conclusão. */
  onConfirmar: (valores: ValoresConclusao, identificadorIdempotente: string) => void;
}

/**
 * D-10: ao abrir, o Radix focaria o primeiro campo e abriria o teclado numérico do tablet. Impedimos o foco
 * automático; o teclado só abre se o operador tocar num campo.
 */
export function impedirFocoAutomatico(evento: { preventDefault: () => void }): void {
  evento.preventDefault();
}

export function tituloConcluir(cartao: { opNumero: string }, modo: ModoConcluir): string {
  return modo === 'registrar' ? `Registrar e avançar — OP-${cartao.opNumero}` : `Concluir — OP-${cartao.opNumero}`;
}

export type TabletConcluirConteudoProps = Omit<TabletConcluirSheetProps, 'modo'> & { modo: ModoConcluir };

/** Corpo da folha (campos, avisos e botões); separado do Sheet para ser testável sem portal. */
export function TabletConcluirConteudo({
  cartao,
  modo,
  identificadorIdempotente,
  enviando,
  erro,
  onFechar,
  onConfirmar,
}: TabletConcluirConteudoProps): ReactElement {
  const iniciais = valoresIniciaisConclusao(cartao);
  const [boa, setBoa] = useState<string>(String(iniciais.quantidadeBoa));
  const [nc, setNc] = useState<string>(String(iniciais.quantidadeNaoConforme));
  const [motivo, setMotivo] = useState<string>('');
  const [validacao, setValidacao] = useState<string | null>(null);
  const [confirmarExcesso, setConfirmarExcesso] = useState<boolean>(false);

  const quantidadeNc = converterQuantidade(nc);
  const temNc = Number.isFinite(quantidadeNc) && quantidadeNc > 0;
  const etapasPuladas = cartao.etapasPuladas ?? [];

  function confirmar(): void {
    const decisao = avaliarConfirmacao({
      boaTexto: boa,
      ncTexto: nc,
      motivo,
      quantidadePendente: cartao.quantidadePendente,
      confirmarExcesso,
    });
    if (decisao.tipo === 'erro') {
      setValidacao(decisao.mensagem);
      return;
    }
    if (decisao.tipo === 'confirmar-excesso') {
      setValidacao(decisao.mensagem);
      setConfirmarExcesso(true);
      return;
    }
    setValidacao(null);
    onConfirmar(decisao.valores, identificadorIdempotente);
  }

  const classeCampo =
    'w-full min-h-[64px] px-4 bg-slate-950 border border-slate-700 rounded-xl text-2xl font-mono text-white focus:ring-2 focus:ring-emerald-400';

  return (
    <div className="space-y-4">
      {modo === 'registrar' && etapasPuladas.length > 0 && (
        <p className="text-sm text-amber-200">Lança registro tardio em: {descreverEtapasPuladas(etapasPuladas)}</p>
      )}
      {modo === 'registrar' && cartao.naoAlocado && <p className="text-sm text-sky-200">{AVISO_CABINE_ALOCADA}</p>}

      <div className="grid grid-cols-2 gap-4">
        <label className="space-y-1 text-xs font-bold uppercase text-slate-300">
          Peças boas
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            value={boa}
            onChange={(e) => {
              setBoa(e.target.value);
              setValidacao(null);
              setConfirmarExcesso(false);
            }}
            className={classeCampo}
          />
        </label>
        <label className="space-y-1 text-xs font-bold uppercase text-slate-300">
          Não conformes
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            value={nc}
            onChange={(e) => {
              setNc(e.target.value);
              setValidacao(null);
              setConfirmarExcesso(false);
            }}
            className={classeCampo}
          />
        </label>
      </div>

      {temNc && (
        <textarea
          aria-label="Motivo da não conformidade"
          value={motivo}
          onChange={(e) => {
            setMotivo(e.target.value);
            setValidacao(null);
          }}
          placeholder="Motivo da não conformidade (obrigatório)"
          rows={3}
          className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-xl text-base text-white focus:ring-2 focus:ring-emerald-400"
        />
      )}

      {(validacao || erro) && (
        <p className="text-sm text-red-300" role="alert">
          {validacao ?? erro}
        </p>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          disabled={enviando}
          onClick={onFechar}
          className="min-h-[64px] px-6 disabled:opacity-50 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-100 font-bold text-base rounded-xl touch-manipulation"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={enviando}
          onClick={confirmar}
          className="min-h-[64px] flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-base rounded-xl touch-manipulation"
        >
          {confirmarExcesso
            ? 'Confirmar mesmo assim'
            : rotuloBotaoConcluir({ quantidadeNaoConforme: temNc ? quantidadeNc : 0 }, modo)}
        </button>
      </div>
    </div>
  );
}

export function TabletConcluirSheet({ modo = 'concluir', ...props }: TabletConcluirSheetProps): ReactElement {
  const { cartao, enviando, onFechar } = props;
  return (
    <Sheet open onOpenChange={(v) => (!v && !enviando ? onFechar() : undefined)}>
      <SheetContent
        side="bottom"
        className="bg-slate-900 text-slate-100 border-slate-700 p-5"
        data-density="touch"
        onOpenAutoFocus={impedirFocoAutomatico}
      >
        <SheetHeader>
          <SheetTitle className="text-xl text-white">{tituloConcluir(cartao, modo)}</SheetTitle>
          <SheetDescription className="text-slate-400">{descricaoConcluir(cartao)}</SheetDescription>
        </SheetHeader>
        <TabletConcluirConteudo modo={modo} {...props} />
      </SheetContent>
    </Sheet>
  );
}
